import React from 'react';
import { BadgeCheck } from 'lucide-react';

/**
 * Build admin auth headers.
 * Cookie-based auth means most requests need NO custom headers — the browser sends
 * the tgm_admin_jwt cookie automatically. The only header we still inject is the
 * master X-Admin-Token (when present) for CLI/curl emergency access.
 */
export const adminAuthHeaders = (extra = {}) => {
  const headers = { ...extra };
  const tok = localStorage.getItem('tgmart_admin_token');
  if (tok) headers['X-Admin-Token'] = tok;
  return headers;
};

/** Render a single table cell value: images become <img>, arrays join with commas, rest stringified. */
export const renderCellValue = (item, key) => {
  if (key === 'image' && item[key]) {
    return <img src={item[key]} alt="" className="w-10 h-10 rounded object-cover" />;
  }
  if (key === 'verified' || key === 'claimed') {
    return item[key]
      ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5"><BadgeCheck className="w-3 h-3" /> {key === 'claimed' ? 'Claimed' : 'Verified'}</span>
      : <span className="text-[10px] uppercase tracking-wider text-gray-400">{key === 'claimed' ? 'Unclaimed' : '—'}</span>;
  }
  // Format timestamp-like fields as a friendly local date+time.
  if ((key === 'created_at' || key === 'updated_at' || key === 'claimed_at') && item[key]) {
    try {
      const d = new Date(item[key]);
      if (!isNaN(d.getTime())) {
        return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      }
    } catch (e) {
      // Non-fatal: fall through to raw string rendering below.
      console.warn('Date-cell render failed for key', key, e);
    }
  }
  if (Array.isArray(item[key])) return item[key].join(', ');
  // Render booleans as Yes/No chips so admin tables don't show raw `true/false`
  // strings. Applies to is_active, verified_only, highlighted, etc.
  if (typeof item[key] === 'boolean') {
    return item[key]
      ? <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5">Yes</span>
      : <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider bg-gray-100 text-gray-500 border border-gray-200 rounded-full px-1.5 py-0.5">No</span>;
  }
  return String(item[key] ?? '');
};

export const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 80);

export const SELECT_OPTIONS = {
  status: [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ],
  customer_type: [
    { value: 'Individual', label: 'Individual' },
    { value: 'Business', label: 'Business' },
  ],
  is_active: [
    { value: true, label: 'Active' },
    { value: false, label: 'Inactive' },
  ],
  priority_listing: [
    { value: false, label: 'No' },
    { value: true, label: 'Yes' },
  ],
  highlighted: [
    { value: false, label: 'No' },
    { value: true, label: 'Yes' },
  ],
  verified_only: [
    { value: false, label: 'No — anyone can self-select' },
    { value: true, label: 'Yes — admin-assigned only' },
  ],
  show_in_top: [
    { value: true, label: 'Yes — show in Top Categories' },
    { value: false, label: 'No — hide from homepage Top Categories' },
  ],
};

export const FIELD_RULES = {
  'customers.name': { required: true, placeholder: 'Full name' },
  'customers.mobile': { required: true, pattern: '^[0-9]{10}$', maxLength: 10, inputMode: 'numeric', placeholder: '10-digit mobile', title: 'Enter 10-digit mobile number' },
  'customers.email': { type: 'email', placeholder: 'name@example.com', title: 'Enter a valid email' },
  'plans.features': { placeholder: 'One feature per line\nBasic vendor listing\nUp to 10 products\nDisplay in 1 category' },
};

export const BUSINESS_TYPES = ['Manufacturer', 'Wholesaler', 'Trader', 'Exporter', 'Service Provider', 'Retailer'];
export const TG_CITIES = ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Khammam', 'Ramagundam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Siddipet', 'Jagtial', 'Sangareddy', 'Medak', 'Vikarabad', 'Other'];
export const UNITS = ['Piece', 'Kilogram', 'Gram', 'Litre', 'Metre', 'Square Meter', 'Box', 'Pack', 'Dozen', 'Tonne', 'Roll', 'Set'];
